import { AuxTabBar } from "@blk/aladdin-react-components-es";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import React, { useEffect } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import "./insGroupedWidgetMenu.scss";
import { useConditionalVisibility } from "../hooks";
import { preview } from "common/constants";
import { widgetFilterTypes } from "components/modal/add-existing-widget/types";
import { ModalName } from "components/modal/types";
import { useCurrentWidget, useWidgetComponentProps } from "components/widgets/hooks/use-current-widget";
import { useDashboardFullPathInformations } from "components/workspaces/hooks/use-current-dashboard";
import { appInsightEvents, language } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { widgetsSelector } from "store/cross-slice/widgetSelector";
import { setCurrentStatusSelectedGroupedWidget } from "store/current-dashboard";
import { ConditionalVisibility } from "store/current-dashboard/types";
import { openModal } from "store/modal";
import { pushIfNeeded, removeWidgetFromGroup, removeWidgetFromSection, reorderWidgetsInGroup, selectedSectionIndexSelector, setDisabledFilters, updateSearchParam, updateWidget, useIsEditMode } from "store/template";
import { getCurrentLocalTime } from "common/date-utils";
const sectionWidgetsSelector = createSelector((state, sectionId) => state.entities.Section?.[sectionId]?.widgets, (sectionWidgets) => Object.entries(sectionWidgets || {})
    .map(([visibility, widgets]) => widgets.map((w) => ({ id: w.id, visibility })))
    .flat());
const visibleWidgetsInGroup = createSelector(widgetsSelector, (state, widgetIds, sectionWidgets) => sectionWidgets, (state, widgetIds, sectionWidgets, conditionalVisibility) => conditionalVisibility, (widgets, sectionWidgets, conditionalVisibility) => widgets.reduce((prev, curr) => {
    const visibility = sectionWidgets.find((w) => w.id === curr.id)?.visibility || "";
    if (curr.id && (!Object.keys(ConditionalVisibility).includes(visibility) || visibility === ConditionalVisibility[conditionalVisibility])) {
        prev[curr.id] = curr;
    }
    return prev;
}, {}));
export const addTelemetry = (currentWidgetId, widgets) => {
    if (Object.values(widgets).filter((w) => w.name === "Benchmark Constituents Table").length > 0) {
        appInsightsTrackEvent({ name: appInsightEvents.customEvents.names.BenchmarkWidgetNavigation,
            properties: { LocalTimestamp: getCurrentLocalTime(),
                selectedTab: Object.values(widgets).filter((w) => w.id === currentWidgetId)
                    .at(0)?.name } });
    }
};
export const InsGroupedWidgetMenu = ({ widgetId, sectionId, groupId }) => {
    const conditionalVisibility = useConditionalVisibility(), { pathInformations, selectedSection } = useDashboardFullPathInformations();
    const currentGroupProps = useWidgetComponentProps(sectionId, groupId), currentProps = useWidgetComponentProps(sectionId, widgetId), dispatch = useDispatch(), isPreviewMode = sectionId === preview.sectionId, sectionWidgets = useSelector((state) => sectionWidgetsSelector(state, sectionId), isEqual), selectedSectionIndex = useSelector((state) => selectedSectionIndexSelector(state, selectedSection)), widgets = useSelector((state) => visibleWidgetsInGroup(state, currentGroupProps.widgets, sectionWidgets, conditionalVisibility));
    const currentWidgetId = Object.keys(widgets || {}).includes(widgetId) ? widgetId : currentGroupProps.default;
    useEffect(() => {
        addTelemetry(currentWidgetId, widgets);
    }, [currentWidgetId, widgets]);
    const currentGroupWidget = useCurrentWidget(sectionId, groupId), currentWidget = useCurrentWidget(sectionId, currentWidgetId);
    const isEditMode = useIsEditMode() && !isPreviewMode;
    if ((!widgets || !currentWidget || !currentGroupProps?.widgets) && !currentProps?.widgets) {
        return React.createElement(React.Fragment, null);
    }
    const handleClick = (event) => {
        dispatch(setCurrentStatusSelectedGroupedWidget({
            groupId, sectionId, widgetId: event.detail.uid
        }));
    };
    const dataMenu = currentGroupProps.widgets
        .filter((selectedWidgetId) => widgets[selectedWidgetId]?.name)
        .map((selectedWidgetId) => ({
        label: widgets[selectedWidgetId].name,
        uid: selectedWidgetId
    }));
    const addWidgetToGroup = () => {
        if (currentGroupWidget) {
            batch(() => {
                dispatch(setDisabledFilters({ disabledFilters: [widgetFilterTypes.widgetType] }));
                dispatch(updateSearchParam({
                    search: { widgetType: [{ checked: true, label: "Grid" }] }
                }));
                dispatch(openModal({
                    content: { id: ModalName[ModalName.AddWidget], metadata: { groupId, sectionId, widgetId }, modalName: ModalName.AddWidget },
                    title: language.en.dashboard.edit.addWidget,
                    type: "action"
                }));
                dispatch(updateWidget(currentGroupWidget));
            });
        }
    };
    const dragWidgetInGroup = (event) => {
        if (currentGroupWidget && groupId) {
            batch(() => {
                dispatch(updateWidget(currentGroupWidget));
                dispatch(reorderWidgetsInGroup({ groupId, newIndex: event.detail.newIndex, oldIndex: event.detail.oldIndex }));
            });
        }
    };
    const deleteWidgetFromGroup = (event) => {
        if (currentGroupWidget && sectionId && groupId && event.detail.uid) {
            if (currentGroupProps.widgets?.length > 1) {
                batch(() => {
                    dispatch(updateWidget(currentGroupWidget));
                    dispatch(removeWidgetFromGroup({ groupId, sectionId, widgetId: event.detail.uid }));
                    // If we delete the current selected tab, set the selection to the next tab
                    if (widgetId === event.detail.uid) {
                        const currentWidgetIndex = currentGroupProps.widgets.indexOf(widgetId);
                        const newSelectedWidget = currentGroupProps.widgets[currentWidgetIndex + 1] ?? currentGroupProps.widgets[currentWidgetIndex - 1];
                        dispatch(setCurrentStatusSelectedGroupedWidget({
                            groupId, sectionId, widgetId: newSelectedWidget
                        }));
                    }
                });
            }
            else {
                // Remove whole group widget
                batch(() => {
                    dispatch(removeWidgetFromSection({ conditionalVisibility, index: 0, sectionId, widgetId: groupId }));
                    pushIfNeeded(dispatch, selectedSectionIndex, selectedSection, pathInformations);
                });
            }
        }
    };
    return React.createElement(AuxTabBar, { key: currentGroupProps?.widgets?.join("-") + isEditMode, data: dataMenu, activeUid: currentWidgetId, tabType: "modern", widthType: "full", isDeletable: isEditMode, onTabBarItemSelected: handleClick, onTabBarAddIconClicked: addWidgetToGroup, onTabBarItemDragged: dragWidgetInGroup, onTabBarItemDeleted: deleteWidgetFromGroup, isDraggable: isEditMode });
};
